import { Component, OnInit, viewChildren } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import { TRemoveConfirmationType } from '@campaign-form/form-fields/creators-and-ctas/cta-details.interface';
import { CtaDetailsService } from '@campaign-form/form-fields/creators-and-ctas/cta-details.service';
import { ECustomFieldType, ICustomValueConfig } from '@core/directives/custom-cta-base.directive';
import { CustomValuesService } from '@core/services/custom-values.service';
import { TranslateModule } from '@ngx-translate/core';
import { CustomRemoveButtonComponent } from '@shared/components/custom-remove-button/custom-remove-button.component';
import { EditableTitleComponent } from '@shared/components/editable-title/editable-title.component';
import { FileUploaderComponent } from '@shared/components/file-uploader/file-uploader.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { imageExtensionValidator } from '@shared/validators/image-extension.validator';
import { FormUtilitiesService } from '@utilities/form-utilities.service';
import { v4 as uuidv4 } from 'uuid';
import { DeliverableAdvancedButtonComponent } from '../../advanced-button/deliverable-advanced-button.component';
import { DeliverableAdvancedSettingsSectionComponent } from '../../advanced-settings-section/deliverable-advanced-settings-section.component';
import { IDeliverableImageForm } from '../../deliverable-list.interface';
import { TextDescriptionDeliverableComponent } from '../text-description-deliverable/text-description-deliverable.component';
import { IImageDeliverableConfig, TImageDeliverableConfigOverrides } from './image-deliverable.interface';

@Component({
  imports: [
    CustomRemoveButtonComponent,
    DeliverableAdvancedButtonComponent,
    DeliverableAdvancedSettingsSectionComponent,
    EditableTitleComponent,
    FileUploaderComponent,
    IconComponent,
    MatButtonModule,
    MatCheckboxModule,
    MatErrorComponent,
    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-image-deliverable',
  templateUrl: './image-deliverable.component.html',
})
export abstract class ImageDeliverableComponent extends TextDescriptionDeliverableComponent implements OnInit {
  public override readonly config: IImageDeliverableConfig;
  private readonly fileUploaders = viewChildren<FileUploaderComponent>('fileUploaderComponent');
  private fileUploaderDeleted = false;

  protected abstract override getConfigOverrides(): TImageDeliverableConfigOverrides;

  protected override get customValueConfig(): ICustomValueConfig {
    return {
      componentFields: {
        getForm: (): FormGroup => this.form(),
        managedFields: [
          { name: this.config.textFieldName, type: ECustomFieldType.string },
          { name: 'id', type: ECustomFieldType.string },
          { name: 'sponsoredContentRelevant', type: ECustomFieldType.boolean },
        ],
      },
      nestedArray: {
        getFormArray: (): FormArray<FormGroup> => this.images,
        managedFields: [
          { name: 'imageId', type: ECustomFieldType.string },
          { name: 'id', type: ECustomFieldType.string },
        ],
      },
    };
  }

  public get images(): FormArray<FormGroup<IDeliverableImageForm>> {
    return this.form().get('images') as FormArray<FormGroup<IDeliverableImageForm>>;
  }

  constructor(customValuesService: CustomValuesService) {
    super(customValuesService);
    const defaultConfig: Omit<IImageDeliverableConfig, 'type'> = {
      addButtonTranslationKey: '',
      highlightNamespace: 'imageUploader',
      placeholderTranslationKey: '',
      subtitleTranslationKey: '',
      textFieldName: 'description',
    };

    this.config = { ...defaultConfig, ...this.getConfigOverrides() };
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.autoOpenAdvancedSettingsIfNeeded(this.platformFeature(), this.config.type);
  }

  public isNewlyAddedImage(id: string): boolean {
    return FormUtilitiesService.isNewlyHighlightedElement(id, this.config.highlightNamespace);
  }

  public addImage(): void {
    const newImageForm = new FormGroup<IDeliverableImageForm>({
      customCtaValues: new FormControl<ICustomCtaValue[]>([], { nonNullable: true }),
      exclusiveTo: new FormControl<string | null>(this.isCustomMode() ? this.creatorId()! : null),
      globalCtaValues: new FormControl<IGlobalCtaValue[] | null>(null),
      id: new FormControl<string | null>(uuidv4()),
      imageFile: new FormControl<File | null>(null, [Validators.required, imageExtensionValidator()]),
      imageId: new FormControl<string | null>(null),
    });

    this.images.push(newImageForm);
    this.initializeNestedForm(newImageForm, [{ name: 'imageId', type: ECustomFieldType.string }]);
    this.trackNestedArrayItemAdded('imageId', this.images.length - 1);
    this.isChildDeleteDisabled.set(false);
    this.lastActiveChildElementDetails.set(
      CtaDetailsService.getLastActiveElementCreatorNames(this.images, this.isCustomMode(), this.creators()),
    );

    FormUtilitiesService.highlightNewElement(newImageForm, this.config.highlightNamespace);
  }

  public removeFileUploader(id: string): void {
    const imageFormIndex = this.images.controls.findIndex((control) => control.get('id')?.value === id);
    const form = this.images.at(imageFormIndex) as FormGroup;

    const dialogRef = CtaDetailsService.openRemoveConfirmationDialog(
      this.dialog,
      this.translate,
      form,
      this.creators(),
      this.config.type as TRemoveConfirmationType,
    );

    if (dialogRef) {
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.removeFileUploaderOperations(id);
        }
      });

      return;
    }

    this.removeFileUploaderOperations(id);
  }

  public onImageSelected(event: { file: File; id: string }, id: string): void {
    this.setImage(event.file, event.id, id);
  }

  public onImageRemoved(id: string): void {
    if (this.fileUploaderDeleted) {
      this.fileUploaderDeleted = false;

      const imageFormIndex = this.images.controls.findIndex((control) => control.get('id')?.value === id);
      const form = this.images.at(imageFormIndex) as FormGroup;
      const removedValue = form.get('imageId')?.value as string | null;

      this.trackNestedArrayItemRemoved('imageId', imageFormIndex, removedValue);
      this.cleanupNestedForm(form);
      this.images.removeAt(imageFormIndex);
      this.checkNestedFormDeleteDisableConditions(this.images);

      return;
    }

    this.setImage(null, null, id);
    this.checkNestedFormDeleteDisableConditions(this.images);
  }

  public override onDeleteClick(exclusiveTo: string | TNil): void {
    const dialogRef = super.onDeleteClick(exclusiveTo);

    if (dialogRef) {
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.deleteOperations();
        }
      });

      return;
    }

    this.deleteOperations();
  }

  private setImage(file: File | null, image: string | null, id: string): void {
    const imageGroup = this.images.controls.find((control) => control.get('id')?.value === id) as FormGroup;

    imageGroup.patchValue({ imageFile: file, imageId: image });
    imageGroup.get('imageFile')?.markAsDirty();
    imageGroup.get('imageId')?.markAsDirty();
    imageGroup.updateValueAndValidity();
  }

  private removeFileUploaderOperations(id: string): void {
    this.fileUploaderDeleted = true;
    this.isChildDeleteDisabled.set(true);

    this.fileUploaders()
      .find((fileUploader) => fileUploader.id() === id)
      ?.onRemoveImage();
  }

  private deleteOperations(): void {
    this.fileUploaders().forEach((uploader) => uploader.onRemoveImage());
    this.delete.emit();
  }
}
